import {
  StaticSiteDataContext,
  useNewMobileExperience,
} from "../../contexts/StaticSiteDataContext";
import {
  Configuration,
  Section,
  SectionTypes,
  WebsiteEvent,
} from "../../resources/modelTypes";
import { Page } from "../../resources/modelTypes";
import { SelectedCollection, SelectedRecord } from "../../resources/selectors";
import SitePreviewContext from "../../website/components/SitePreviewContext";
import { sortRecordsByCreatedAtAndPosition } from "../../website/utils";
import { selectors } from "../cypressSelectors";
import { PageSlugs } from "../types";
import { DisplayEvent } from "./DisplayEvent";
import { DisplayRSVP } from "./DisplayRSVP";
import { DisplaySectionWebsite } from "./DisplaySection";
import { DisplayWrapper } from "./DisplayWrapper";
import {
  AttendantSection,
  DisplayAttendantSections,
} from "./section-types/DisplayAttendantSection";
import { DisplayDividerSection } from "./section-types/DisplayDividerSection";
import { DisplayMap } from "./section-types/DisplayMap";
import { css } from "@emotion/react";
import { MediumHeadline } from "@minted/fancyclothes";
import { styleUtils, theme } from "@minted/minted-components";
import React, { Fragment, memo, useContext, useMemo } from "react";

const displayWebsiteContentStyles = {
  oiEventDetailsHeader: css`
    display: grid;
    justify-content: center;
    margin-bottom: ${theme.spacing.x4};
    margin-top: ${styleUtils.rem(80)};
    text-align: center;
  `,
  oiRegistryHeader: css`
    margin-top: ${theme.spacing.x4};
  `,
};

export type DisplayWebsiteContentProps = {
  configuration: Configuration;
  eventCollection: SelectedCollection<WebsiteEvent> | null;
  page: Page;
  sectionCollection: SelectedCollection<Section>;
};

const DisplayWebsiteContent = ({
  configuration,
  eventCollection,
  page,
  sectionCollection,
}: DisplayWebsiteContentProps) => {
  const { isOnlineInvitationSite, isWeddingSite } = useContext(
    StaticSiteDataContext,
  );
  const isPreviewSectionClickable = useNewMobileExperience();

  let allItems: (SelectedRecord<WebsiteEvent> | SelectedRecord<Section>)[];
  const stagedSection = sectionCollection.resource.staged?.create?.[0];
  const stagedPageId = stagedSection?.data?.page;
  const sectionCollectionPageId = sectionCollection.params.page;
  const stagedEvent = eventCollection?.resource.staged?.create[0];
  const eventPageName = eventCollection?.resource?.name;
  const hasStagedCreation = stagedSection || stagedEvent;
  const { onSectionEditClick } = useContext(SitePreviewContext);

  if (eventCollection?.items) {
    allItems = [...eventCollection.items, ...sectionCollection.items];

    if (stagedEvent && eventPageName === PageSlugs.Events) {
      allItems.push(stagedEvent);
    }
  } else {
    allItems = sectionCollection.items;
  }

  if (stagedSection && stagedPageId === sectionCollectionPageId) {
    allItems.push(stagedSection);
  }

  sortRecordsByCreatedAtAndPosition(allItems);

  const extraComponentsForSlug = useMemo(() => {
    if (page.slug === PageSlugs.RSVP) {
      return <DisplayRSVP configuration={configuration} isPreview={false} />;
    }

    return null;
  }, [configuration, page.slug]);

  const sectionElements: React.ReactNode[] = useMemo(() => {
    let hasSeenRegistry = false;
    const elements: React.ReactNode[] = [];

    for (let index = 0; index < allItems.length; index++) {
      const current = allItems[index] as
        | SelectedRecord<WebsiteEvent>
        | SelectedRecord<Section>;
      const isEvent = !("sectionType" in current.data);

      if (isEvent) {
        const event = current.data as WebsiteEvent;

        if (event.private) {
          continue;
        }

        elements.push(
          <div
            data-cy={`${selectors.website_displaySection}_event`}
            id={`section-${event.id || "new"}`}
            key={`collapsed${event.id}`}
            onClick={
              hasStagedCreation || !isPreviewSectionClickable
                ? undefined
                : () => onSectionEditClick(event.id, "event")
            }
          >
            {isOnlineInvitationSite && (
              <div css={displayWebsiteContentStyles.oiEventDetailsHeader}>
                <MediumHeadline>Event Details</MediumHeadline>
              </div>
            )}
            <DisplayEvent configuration={configuration} event={event} />
            {configuration.showPageDividers && (
              <DisplayDividerSection hideLast={index === allItems.length - 1} />
            )}
          </div>,
        );
        continue;
      }

      const section = current.data as Section;

      if (section.sectionType === SectionTypes.Attendant) {
        const attendantGroup = [section] as AttendantSection[];

        const nextSection = allItems[index + 1]?.data as AttendantSection;

        // If next section is also an Attendant, group them for potential grid display
        if (nextSection && nextSection.sectionType === SectionTypes.Attendant) {
          attendantGroup.push(nextSection);
          index++;
        }

        elements.push(
          <DisplayAttendantSections
            configuration={configuration}
            page={page}
            sections={attendantGroup}
          />,
        );
        continue;
      }

      if (
        isOnlineInvitationSite &&
        section.sectionType === SectionTypes.Registry &&
        !hasSeenRegistry
      ) {
        hasSeenRegistry = true;
        elements.push(
          <div css={displayWebsiteContentStyles.oiRegistryHeader}>
            <MediumHeadline>Gift Registry</MediumHeadline>
          </div>,
        );
      }

      const shouldDisplayDivider =
        configuration.showPageDividers &&
        [
          SectionTypes.Accommodation,
          SectionTypes.Registry,
          SectionTypes.CashRegistry,
        ].includes(section.sectionType);

      // All other section types displayed here
      elements.push(
        <Fragment key={`collapsed${section.id}`}>
          <div
            data-cy={`${selectors.website_displaySection}_${section.sectionType}`}
            id={`section-${section.id || "new"}`}
            onClick={
              hasStagedCreation || !isPreviewSectionClickable
                ? undefined
                : () => onSectionEditClick(section.id, "section")
            }
          >
            <DisplaySectionWebsite
              configuration={configuration}
              section={section}
            />
          </div>
          {shouldDisplayDivider && <DisplayDividerSection hideLast />}
        </Fragment>,
      );
    }

    return elements;
  }, [
    allItems,
    configuration,
    isOnlineInvitationSite,
    hasStagedCreation,
    isPreviewSectionClickable,
    onSectionEditClick,
    page,
  ]);

  return (
    <DisplayWrapper textAlignment={configuration.textAlignment}>
      {extraComponentsForSlug}
      {sectionElements}
      {isWeddingSite &&
        page.slug === PageSlugs.Events &&
        configuration.showMap &&
        eventCollection && <DisplayMap events={eventCollection.items} />}
    </DisplayWrapper>
  );
};

const MemoizedDisplayWebsiteContent = memo(DisplayWebsiteContent);

export { MemoizedDisplayWebsiteContent as DisplayWebsiteContent };
